import type { LootPile } from "../engine/inventory/LootPile.js";
import type { Position } from "../engine/types/grid.js";

/**
 * E12 — session store for loot piles that exist on the current map.
 *
 * Kept intentionally tiny: piles are produced by `CombatManager` (enemy
 * death) and `pickpocket` flows, consumed by the loot-interaction UI.
 * Piles are keyed by their own ID; callers typically look them up by
 * position when the player steps on / clicks a cell.
 *
 * Not map-keyed — the runtime clears the store on map teardown. This
 * matches `WorldStore.activeMap`'s single-map-at-a-time contract and
 * keeps save serialization simple.
 */
const piles = new Map<string, LootPile>();

export const LootPileStore = {
  /** Store a freshly-spawned pile. Replaces any previous pile with the same id. */
  put(pile: LootPile): void {
    piles.set(pile.id, pile);
  },

  /** Replace a pile in-place (e.g. after `takeStack`). Removes the pile if empty. */
  update(pile: LootPile): void {
    if (pile.stacks.length === 0) {
      piles.delete(pile.id);
      return;
    }
    piles.set(pile.id, pile);
  },

  get(id: string): LootPile | undefined {
    return piles.get(id);
  },

  /** Return the first pile at the given cell, if any. */
  atCell(cell: Position): LootPile | undefined {
    for (const p of piles.values()) {
      if (p.position.x === cell.x && p.position.z === cell.z) return p;
    }
    return undefined;
  },

  all(): readonly LootPile[] {
    return [...piles.values()];
  },

  remove(id: string): void {
    piles.delete(id);
  },

  /** Called on map teardown. Drops every pile without emitting events. */
  clear(): void {
    piles.clear();
  },
};
